import { 
  collection, 
  getDocs, 
  getDoc, 
  doc, 
  query, 
  where, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  orderBy, 
  limit,
  setDoc
} from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';

// Interfaces for missing collections
export interface Coupon {
  id: string;
  code: string;
  discountType: 'percentage' | 'fixed';
  discountValue: number;
  expirationDate: Date;
  isActive: boolean;
  usageLimit?: number;
  usedCount?: number;
  createdAt?: Date | any; // Added to handle Firestore timestamp
}

export interface WishlistItem {
  id: string;
  userId: string;
  productId: string;
  addedAt: Date;
}

export interface Notification {
  id: string;
  userId: string;
  title: string;
  message: string;
  type: 'order' | 'promotion' | 'general';
  isRead: boolean;
  createdAt: Date;
}

export interface Inventory {
  id: string;
  productId: string;
  stockHistory: Array<{
    date: Date;
    change: number;
    reason: string;
    previousStock: number;
    newStock: number;
  }>;
}

export interface ProductReview {
  id: string;
  productId: string;
  userId: string;
  userName: string;
  rating: number;
  comment: string;
  createdAt: Date;
  isVerifiedPurchase: boolean;
}

export interface ShippingProvider {
  id: string;
  name: string;
  description: string;
  cost: number;
  estimatedDeliveryTime: string;
  isActive: boolean;
}

export interface PaymentMethod {
  id: string;
  userId: string;
  type: 'credit_card' | 'bank_transfer' | 'paypal' | 'other';
  details: string; // Masked card number or other details
  isDefault: boolean;
}

export interface Referral {
  id: string;
  referrerId: string;
  refereeId: string;
  code: string;
  rewardAmount: number;
  isUsed: boolean;
  createdAt: Date;
  usedAt?: Date;
}

export interface NewsletterSubscriber {
  id: string;
  email: string;
  subscribedAt: Date;
  isActive: boolean;
  isVerified: boolean;
}

export interface FAQ {
  id: string;
  question: string;
  answer: string;
  category: string;
  isActive: boolean;
}

export interface ContactMessage {
  id: string;
  name: string;
  email: string;
  subject: string;
  message: string;
  status: 'new' | 'in_progress' | 'resolved';
  createdAt: Date;
  respondedAt?: Date;
}

export interface SearchHistory {
  id: string;
  userId?: string; // Optional for non-logged in users
  searchTerm: string;
  searchedAt: Date;
  resultsCount: number;
  sessionId: string; // To track anonymous users
}

// Initialize site settings if it doesn't exist
export const initializeSiteSettings = async () => {
  try {
    const settingsDocRef = doc(db, 'settings', 'site');
    const settingsDoc = await getDoc(settingsDocRef);
    
    if (!settingsDoc.exists()) {
      // Create default settings
      const defaultSettings = {
        siteTitle: 'Rölyef Boyama Setleri',
        siteDescription: 'Kaliteli ve eğlenceli boya setleri',
        logoUrl: '',
        faviconUrl: '',
        freeShippingThreshold: 750,
        orderProcessingTime: '2-3 iş günü',
        contactEmail: '',
        contactPhone: '',
        currency: 'TRY',
        currencySymbol: '₺',
        defaultLanguage: 'tr',
        analyticsId: '',
        facebookPixelId: '',
        googleTagManagerId: '',
        newsletterEnabled: true,
        googleAnalyticsId: '',
        privacyPolicy: '',
        termsOfService: '',
        refundPolicy: ''
      };
      
      await setDoc(settingsDocRef, defaultSettings);
      console.log('Default site settings created successfully');
    } else {
      console.log('Site settings already exist');
    }
  } catch (error) {
    console.error('Error initializing site settings:', error);
    throw error;
  }
};

// Coupon operations
export const getCoupons = async (): Promise<Coupon[]> => {
  try {
    const couponsCollection = collection(db, 'coupons');
    // Temporary fix: fetch all coupons since composite index is required for isActive + createdAt query
    const couponsQuery = query(couponsCollection);
    const couponsSnapshot = await getDocs(couponsQuery);

    // Filter active coupons and sort by createdAt client-side
    let coupons = couponsSnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        expirationDate: data.expirationDate?.toDate ? data.expirationDate.toDate() : data.expirationDate
      } as Coupon;
    });

    // Filter by isActive and sort by createdAt desc
    coupons = coupons.filter(coupon => coupon.isActive);
    coupons.sort((a, b) => {
      // Handle createdAt safely
      let dateA = null;
      let dateB = null;

      if (a.createdAt) {
        dateA = a.createdAt.toDate ? a.createdAt.toDate() : (a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt));
      }
      if (b.createdAt) {
        dateB = b.createdAt.toDate ? b.createdAt.toDate() : (b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt));
      }

      if (dateA && dateB) {
        return dateB.getTime() - dateA.getTime(); // Descending order
      } else if (dateA) {
        return -1; // Place items with dateA first
      } else if (dateB) {
        return 1;  // Place items with dateB first
      }
      return 0;
    });

    return coupons;
  } catch (error) {
    console.error('Error fetching coupons:', error);
    throw error;
  }
};

export const getCouponByCode = async (code: string): Promise<Coupon | null> => {
  try {
    const couponsCollection = collection(db, 'coupons');
    // Temporary fix: fetch all coupons since composite index is required for code + isActive query
    const couponsQuery = query(couponsCollection);
    const couponsSnapshot = await getDocs(couponsQuery);

    // Find coupon with matching code and isActive = true
    for (const doc of couponsSnapshot.docs) {
      const data = doc.data();
      const coupon = {
        id: doc.id,
        ...data,
        expirationDate: data.expirationDate?.toDate ? data.expirationDate.toDate() : data.expirationDate
      } as Coupon;

      if (coupon.code?.toUpperCase() === code.toUpperCase() && coupon.isActive) {
        return coupon;
      }
    }
    return null;
  } catch (error) {
    console.error('Error fetching coupon by code:', error);
    throw error;
  }
};

export const addCoupon = async (couponData: Omit<Coupon, 'id'>) => {
  try {
    const couponsCollection = collection(db, 'coupons');
    const docRef = await addDoc(couponsCollection, {
      ...couponData,
      createdAt: new Date()
    });
    return docRef.id;
  } catch (error) {
    console.error('Error adding coupon:', error);
    throw error;
  }
};

// Wishlist operations
export const getUserWishlist = async (userId: string): Promise<WishlistItem[]> => {
  try {
    const wishlistRef = collection(db, `users/${userId}/wishlist`);
    const wishlistSnapshot = await getDocs(wishlistRef);

    return wishlistSnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        userId,
        ...data,
        addedAt: data.addedAt?.toDate ? data.addedAt.toDate() : data.addedAt
      } as WishlistItem;
    });
  } catch (error) {
    console.error('Error fetching wishlist:', error);
    throw error;
  }
};

export const addToWishlist = async (userId: string, productId: string) => {
  try {
    const wishlistRef = collection(db, `users/${userId}/wishlist`);
    // Check if item already exists in wishlist
    const existingQuery = query(wishlistRef, where('productId', '==', productId));
    const existingSnapshot = await getDocs(existingQuery);
    
    if (!existingSnapshot.empty) {
      // Item already in wishlist
      return existingSnapshot.docs[0].id;
    }
    
    const docRef = await addDoc(wishlistRef, {
      productId,
      addedAt: new Date()
    });
    return docRef.id;
  } catch (error) {
    console.error('Error adding to wishlist:', error);
    throw error;
  }
};

export const removeFromWishlist = async (userId: string, wishlistId: string) => {
  try {
    const wishlistDoc = doc(db, `users/${userId}/wishlist`, wishlistId);
    await deleteDoc(wishlistDoc);
  } catch (error) {
    console.error('Error removing from wishlist:', error);
    throw error;
  }
};

// Notification operations
export const getUserNotifications = async (userId: string): Promise<Notification[]> => {
  try {
    const notificationsRef = collection(db, `users/${userId}/notifications`);
    const notificationsQuery = query(notificationsRef, orderBy('createdAt', 'desc'));
    const notificationsSnapshot = await getDocs(notificationsQuery);

    return notificationsSnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        userId,
        ...data,
        createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt
      } as Notification;
    });
  } catch (error) {
    console.error('Error fetching notifications:', error);
    throw error;
  }
};

export const addNotification = async (userId: string, notificationData: Omit<Notification, 'id' | 'createdAt'>) => {
  try {
    const notificationsRef = collection(db, `users/${userId}/notifications`);
    const docRef = await addDoc(notificationsRef, {
      ...notificationData,
      createdAt: new Date()
    });
    return docRef.id;
  } catch (error) {
    console.error('Error adding notification:', error);
    throw error;
  }
};

export const markNotificationAsRead = async (userId: string, notificationId: string) => {
  try {
    const notificationDoc = doc(db, `users/${userId}/notifications`, notificationId);
    await updateDoc(notificationDoc, {
      isRead: true
    });
  } catch (error) {
    console.error('Error marking notification as read:', error);
    throw error;
  }
};

// Newsletter subscriber operations
export const addNewsletterSubscriber = async (email: string): Promise<string> => {
  try {
    const subscribersCollection = collection(db, 'subscribers');
    // Check if subscriber already exists
    const existingQuery = query(subscribersCollection, where('email', '==', email));
    const existingSnapshot = await getDocs(existingQuery);
    
    if (!existingSnapshot.empty) {
      // Update existing subscriber
      const docId = existingSnapshot.docs[0].id;
      const subscriberDoc = doc(db, 'subscribers', docId);
      await updateDoc(subscriberDoc, {
        isActive: true,
        subscribedAt: new Date()
      });
      return docId;
    }
    
    const docRef = await addDoc(subscribersCollection, {
      email,
      subscribedAt: new Date(),
      isActive: true,
      isVerified: false
    });
    return docRef.id;
  } catch (error) {
    console.error('Error adding newsletter subscriber:', error);
    throw error;
  }
};

// Contact message operations
export const addContactMessage = async (messageData: Omit<ContactMessage, 'id' | 'createdAt' | 'status'>): Promise<string> => {
  try {
    const messagesCollection = collection(db, 'contactMessages');
    const docRef = await addDoc(messagesCollection, {
      ...messageData,
      createdAt: new Date(),
      status: 'new'
    });
    return docRef.id;
  } catch (error) {
    console.error('Error adding contact message:', error);
    throw error;
  }
};

// FAQ operations
export const getFAQs = async (category?: string): Promise<FAQ[]> => {
  try {
    let faqsQuery;
    if (category) {
      faqsQuery = query(collection(db, 'faqs'), where('category', '==', category), where('isActive', '==', true));
    } else {
      faqsQuery = query(collection(db, 'faqs'), where('isActive', '==', true));
    }
    
    const faqsSnapshot = await getDocs(faqsQuery);

    return faqsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as FAQ[];
  } catch (error) {
    console.error('Error fetching FAQs:', error);
    throw error;
  }
};

// Search history operations
export const addSearchHistory = async (searchData: Omit<SearchHistory, 'id' | 'searchedAt'>): Promise<string> => {
  try {
    const searchHistoryCollection = collection(db, 'searchHistory');
    const docRef = await addDoc(searchHistoryCollection, {
      ...searchData,
      searchedAt: new Date()
    });
    return docRef.id;
  } catch (error) {
    console.error('Error adding search history:', error);
    throw error;
  }
};

// Product reviews operations
export const getProductReviews = async (productId: string): Promise<ProductReview[]> => {
  try {
    const reviewsCollection = collection(db, 'productReviews');
    const q = query(reviewsCollection, where('productId', '==', productId), orderBy('createdAt', 'desc'));
    const reviewsSnapshot = await getDocs(q);

    return reviewsSnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt
      } as ProductReview;
    });
  } catch (error) {
    console.error('Error fetching product reviews:', error);
    throw error;
  }
};

export const addProductReview = async (reviewData: Omit<ProductReview, 'id' | 'createdAt'>): Promise<string> => {
  try {
    const reviewsCollection = collection(db, 'productReviews');
    const docRef = await addDoc(reviewsCollection, {
      ...reviewData,
      createdAt: new Date()
    });
    return docRef.id;
  } catch (error) {
    console.error('Error adding product review:', error);
    throw error;
  }
};

// Export all functions
export {
  addDoc,
  updateDoc,
  deleteDoc,
  collection,
  doc
};